import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { DELETE_PROP_CONSTANT } from "@/store/zustand/changeLedgerTypes";

const EMPTY_VALUE_CONSTANT = "__empty__";

// TODO: Make the typings for this editor more directly just matching what a typescirpt type supports,
// and then do the logic of "what makes sense as the visual control for this" into this component,
// rather than doing it in the manager and this being more dumb taking in directly what it controls.

type ControlType = "text" | "number" | "boolean" | "select" | "object";

type ControlBase = {
  type?: ControlType;
  disable?: boolean;
  description?: string;
  optional?: boolean;
  placeholder?: string;
};

type Control = ControlBase &
  (
    | {
        type: "select";
        options?: (string | number | boolean)[];
        labels?: {
          [key: string]: string;
        };
      }
    | {
        type: "number";
        max?: number;
        min?: number;
        step?: number;
      }
    | {
        type: "text";
      }
    | {
        type: "boolean";
      }
    | {
        type: "object";
      }
  );

export type ControlsSpec = Record<string, Control>;
export type ControlValue =
  | string
  | number
  | boolean
  | Record<string, any>
  | Array<any>
  | undefined
  | null;
export type ControlValues = Record<string, ControlValue>;

interface ControlEditorProps {
  controls: ControlsSpec;
  values: ControlValues;
  onChange: (changes: Record<string, ControlValue>) => void;
  unsavedKeys?: string[];
}

// Custom Toggle component
const Toggle = ({
  checked,
  onCheckedChange,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onCheckedChange(!checked);
    }
  };

  return (
    <button
      className={`flex w-[34px] h-5 items-center p-px relative rounded-[5px] ${
        checked ? "bg-[#3C2B8F] justify-end" : "bg-[#4B5563] justify-start"
      }`}
      onClick={handleToggle}
      disabled={disabled}
      style={{ outline: "none", border: "none" }}
    >
      <div
        className={`flex w-[19px] h-[18px] items-center justify-center relative rounded ${
          checked ? "bg-[#6448EF]" : "bg-white"
        }`}
      >
        {checked && <Check className="w-3 h-3" />}
      </div>
    </button>
  );
};

const Check = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 3L4.5 8.5L2 6"
        stroke="#F5F5F6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Validate value against control type and placeholder constraints
const validateValue = (value: string, control: Control): string | null => {
  if (!value || !control.placeholder) return null;

  // For text inputs, check if it matches any of the expected types
  if (control.type === "text" && control.placeholder) {
    const types = control.placeholder.split(" | ").map((t) => t.trim());

    // Check if value matches any literal types (quoted strings or numbers)
    const matchesLiteral = types.some((type) => {
      // String literal like "default"
      if (type.startsWith('"') && type.endsWith('"')) {
        return value === type.slice(1, -1);
      }
      // Number literal
      if (!isNaN(Number(type))) {
        return value === type;
      }
      return false;
    });

    if (matchesLiteral) return null;

    // Check if value matches primitive type constraints
    const hasString = types.includes("string");
    const hasNumber = types.includes("number");
    const hasBoolean = types.includes("boolean");

    // If it's only literals and none matched, invalid
    if (!hasString && !hasNumber && !hasBoolean && types.length > 0) {
      return `Must be one of: ${types.join(", ")}`;
    }

    // If number is expected but value is not a valid number
    if (hasNumber && !hasString && isNaN(Number(value))) {
      return "Must be a number";
    }

    // If boolean is expected
    if (hasBoolean && !hasString && !hasNumber) {
      if (value !== "true" && value !== "false") {
        return "Must be true or false";
      }
    }
  }

  // For number inputs, validate it's a valid number
  if (control.type === "number") {
    if (isNaN(Number(value))) {
      return "Must be a valid number";
    }
  }

  return null;
};

// Individual control field component
const ControlField = ({
  name,
  control,
  value,
  onChange,
  unsavedKeys = [],
}: {
  name: string;
  control: Control;
  value: ControlValue;
  onChange: (value: ControlValue) => void;
  unsavedKeys?: string[];
}) => {
  const [localValue, setLocalValue] = useState(() => {
    if (control.type === "text" || control.type === "number") {
      return value?.toString() || "";
    }
    return "";
  });
  const [committedValue, setCommittedValue] = useState(() => {
    if (control.type === "text" || control.type === "number") {
      return value?.toString() || "";
    }
    return "";
  });
  const [localBooleanValue, setLocalBooleanValue] = useState(Boolean(value));
  const [localSelectValue, setLocalSelectValue] = useState(() => {
    if (control.type === "select") {
      return value?.toString() || "";
    }
    return "";
  });
  const [localObjectValue, setLocalObjectValue] = useState(() => {
    if (control.type === "object" && value) {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return JSON.stringify({}, null, 2);
      }
    }
    return "{}";
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  // Update local values when prop value changes
  useEffect(() => {
    if (control.type === "text" || control.type === "number") {
      setLocalValue(value?.toString() || "");
      setCommittedValue(value?.toString() || "");
    }

    if (control.type === "boolean") {
      setLocalBooleanValue(Boolean(value));
    }

    if (control.type === "select") {
      setLocalSelectValue(value?.toString() || "");
    }

    if (control.type === "object") {
      if (value) {
        try {
          setLocalObjectValue(JSON.stringify(value, null, 2));
        } catch {
          setLocalObjectValue(JSON.stringify({}, null, 2));
        }
      } else {
        setLocalObjectValue("{}");
      }
    }
  }, [value, control.type]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleTextChange(localValue);
    }
  };

  const handleInputChange = (newValue: string) => {
    setLocalValue(newValue);
    // Validate on input for immediate feedback
    const error = validateValue(newValue, control);
    setValidationError(error);
  };

  const handleTextChange = (newValue: string) => {
    // Validate the new value
    const error = validateValue(newValue, control);
    setValidationError(error);

    // Don't submit if there's a validation error
    if (error) {
      return;
    }

    setLocalValue(newValue);
    if (control.type === "number") {
      const numValue = parseFloat(newValue);
      if (!isNaN(numValue)) {
        let clamped = numValue;
        if (control.min !== undefined && clamped < control.min)
          clamped = control.min;
        if (control.max !== undefined && clamped > control.max)
          clamped = control.max;
        onChange(clamped);
        setLocalValue(clamped.toString());
        setCommittedValue(clamped.toString());
        setValidationError(null);
        return;
      }
    } else {
      onChange(newValue);
    }
    setCommittedValue(newValue);
    setValidationError(null);
  };

  const handleBooleanChange = (newValue: boolean) => {
    setLocalBooleanValue(newValue);
    onChange(newValue);
  };

  const handleSelectChange = (newValue: string) => {
    setLocalSelectValue(newValue);
    onChange(newValue);
  };

  // Check if text/number value has uncommitted changes
  const hasUncommittedChanges =
    ((control.type === "text" || control.type === "number") &&
      localValue !== committedValue) ||
    unsavedKeys.includes(name);

  // Determine if number is out of range for styling
  const isOutOfRange =
    control.type === "number" &&
    localValue !== "" &&
    !isNaN(parseFloat(localValue)) &&
    ((control.min !== undefined && parseFloat(localValue) < control.min) ||
      (control.max !== undefined && parseFloat(localValue) > control.max));

  const renderControl = () => {
    switch (control.type) {
      case "object":
        return (
          <textarea
            value={localObjectValue}
            disabled={true}
            className="w-full bg-[#1F1F22] text-secondary-700 text-xs border-0 font-mono p-2 rounded"
            readOnly
          />
        );

      case "select":
        return (
          <Select
            value={localSelectValue}
            onValueChange={(newValue) => {
              if (newValue === EMPTY_VALUE_CONSTANT && control.optional) {
                setLocalSelectValue("");
                onChange(DELETE_PROP_CONSTANT);
              } else {
                handleSelectChange(newValue);
              }
            }}
            disabled={control.disable}
          >
            <SelectTrigger className="w-full bg-[#1F1F22] text-white text-xs border-0">
              <SelectValue placeholder="Select a value" />
            </SelectTrigger>
            <SelectContent className="bg-[#1F1F22] border-0">
              {control.options
                ?.filter((option) => option !== "") // Filter out empty strings (Radix UI Select requires non-empty values)
                .map((option) => {
                  const optionStr = option.toString();
                  const label = control.labels?.[optionStr] || optionStr;
                  return (
                    <SelectItem
                      key={optionStr}
                      value={optionStr}
                      className="text-white text-xs"
                    >
                      {label}
                    </SelectItem>
                  );
                })}
              {control.optional && (
                <SelectItem
                  value={EMPTY_VALUE_CONSTANT}
                  className="text-gray-500 text-xs"
                >
                  Clear selection
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        );

      case "number":
        return (
          <div className="relative w-full" title={validationError || undefined}>
            <Input
              type="number"
              value={localValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={() => handleTextChange(localValue)}
              onKeyDown={handleKeyDown}
              min={control.min}
              max={control.max}
              step={control.step}
              disabled={control.disable}
              placeholder={control.placeholder}
              className={`w-full bg-[#1F1F22] placeholder-gray-400 text-xs ${
                validationError ? "border border-red-500" : "border-0"
              } ${isOutOfRange ? "text-red-400" : "text-white"} ${
                control.optional && localValue ? "pr-8" : ""
              }`}
            />
            {control.optional && localValue && !validationError && (
              <X
                className="absolute right-2 top-1/2 -translate-y-1/2 size-3 text-secondary-700 hover:text-white cursor-pointer"
                onClick={() => {
                  setLocalValue("");
                  onChange(DELETE_PROP_CONSTANT);
                  setValidationError(null);
                }}
              />
            )}
          </div>
        );

      case "boolean":
        return (
          <Toggle
            checked={localBooleanValue}
            onCheckedChange={handleBooleanChange}
            disabled={control.disable}
          />
        );

      case "text":
      default:
        return (
          <div className="relative w-full" title={validationError || undefined}>
            <Input
              value={localValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={() => handleTextChange(localValue)}
              onKeyDown={handleKeyDown}
              disabled={control.disable}
              placeholder={control.placeholder}
              className={`w-full bg-[#1F1F22] text-white placeholder-gray-400 text-xs ${
                validationError ? "border border-red-500" : "border-0"
              } ${control.optional && localValue ? "pr-8" : ""}`}
            />
            {control.optional && localValue && !validationError && (
              <X
                className="absolute right-2 top-1/2 -translate-y-1/2 size-3 text-secondary-700 hover:text-white cursor-pointer"
                onClick={() => {
                  setLocalValue("");
                  onChange(DELETE_PROP_CONSTANT);
                  setValidationError(null);
                }}
              />
            )}
          </div>
        );
    }
  };

  return (
    <div className="w-full" data-testid={`control-field-${name}`}>
      <div
        className="text-[10px] text-[#9F9FA4] font-medium mb-1 pl-1 inline-flex items-center pb-1"
        style={{ lineHeight: "12px" }}
        title={control.description}
      >
        <span>{name}</span>
        {hasUncommittedChanges && (
          <span className="text-orange-400 ml-1 align-middle">*</span>
        )}
      </div>
      <div data-testid={`control-field-${name}-input`}>{renderControl()}</div>
    </div>
  );
};

export const ControlEditor: React.FC<ControlEditorProps> = ({
  controls,
  values,
  onChange,
  unsavedKeys,
}) => {
  return (
    <div className="space-y-3 w-full">
      {Object.entries(controls).map(([key, control]) => (
        <ControlField
          key={key}
          name={key}
          control={control}
          value={values[key]}
          onChange={(value) => onChange({ [key]: value })}
          unsavedKeys={unsavedKeys}
        />
      ))}
    </div>
  );
};
